import React, { useEffect, useMemo, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { format } from "date-fns";
import useAxiosPrivate from "../../hooks/useAxiosPrivate";
import useAmsConfig from "../../hooks/useAmsConfig";
import AdsAPIs from "../../apiRoutes/Ads";
import AgencyAPIs from "../../apiRoutes/Agency";
import CategoryTrackMappingAPIs from "../../apiRoutes/CategoryTrackMapping";
import {
  GET_AGENCIES_QUERY_KEY,
  GET_AD_REQUESTS_QUERY_KEY,
  GET_AD_REQUEST_QUERY_KEY,
  GET_AD_DELIVERIES_QUERY_KEY,
  GET_CTM_OPTIONS_QUERY_KEY,
} from "../../utils/constants/queryKeys";
import { DUE_DATE_END_MONTH } from "../../utils/constants/AdRequest";
import {
  buildDeliveryAdName,
  buildRequestRemarks,
  composeModule,
  explainName,
  isLegacyName,
  sanitizeToken,
  SOURCE_AGENCY,
  SOURCE_AUTO,
} from "../../utils/adNameBuilder";
import { DatePicker } from "../ui/date-picker";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { Button } from "../ui/button";
import CustomModal from "../common/CustomModal";
import { Combobox } from "../ui/combobox";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Switch } from "../ui/switch";
import toast from "react-hot-toast";
import useZustandStore from "../../zustandStore/useZustandStore";
import usePermissions from "../../hooks/usePermissions";
import useGlobalAppAndLanguage from "../../hooks/useGlobalAppAndLanguage";
import { AGENCY_LIST_PERMISSIONS } from "../../utils/constants/permissions";
import {
  agencyDisplayName,
  getAgencyIdFromUser,
  isAgencyScopedUser,
} from "../../utils/agencyScope";
import { validateAssetUrl } from "../AdDeliver/utils/validateAssetUrl";
import { creatorRequired } from "./utils/directRows";

const OTHER = "__other__";
const HOOKS = Array.from({ length: 10 }, (_, i) => i + 1);

const INITIAL_FORM = {
  for_app_name: "",
  language: "",
  account: "",
  requested_for_agency_id: "",
  category: "",
  script_writer_name: "",
  track: "",
  track_number: "",
  topic: "",
  script_name: "",
  module_type: "",
  module: [],
  creative_base: "",
  creative_base_other: "",
  creative_variation: [],
  visual_base: "",
  visual_base_other: "",
  visual_variation: [],
  human_ai: "",
  script_type: "",
  hooks_count: "",
  priority: "low",
  due_delivery_date: null,
  comments: "",
  script: "",
  reference_link: "",
  remarks: "",
  auto_launch: true,
  // Direct mode: the agency creates and delivers in one go.
  requester_name: "",
  ad_type_id: "",
  creator_name: "",
  editor_name: "",
  hook_links: [],
};

const Req = () => <span className="text-red-500">*</span>;
const textareaCls =
  "flex w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring";

// "<base>x<variation>" as the backend stores it; Other picks use their free text.
const pickValue = (f, key) => (f[key] === OTHER ? f[`${key}_other`] : f[key]);
const composeTwoLevel = (f, prefix) => {
  const base = sanitizeToken(pickValue(f, `${prefix}_base`));
  const variations = [
    ...new Set(
      (f[`${prefix}_variation`] || []).map(sanitizeToken).filter(Boolean),
    ),
  ];
  return base && variations.length ? [base, ...variations].join("x") : base;
};

// Stored value not in the config options = free text; with no options loaded yet, keep it and re-split later.
const splitOption = (value, options) =>
  !value || !options.length || options.some((o) => o.value === value)
    ? [value || "", ""]
    : [OTHER, value];

// Stored "<base>x<v1>x<v2>" -> base (Other when unknown) + variation list; unknown variations stay as custom chips.
function splitTwoLevel(token, prefix, bases, variationsFor) {
  const empty = {
    [`${prefix}_base`]: "",
    [`${prefix}_base_other`]: "",
    [`${prefix}_variation`]: [],
  };
  if (!token) return empty;
  const known = bases.find(
    (o) => token === o.value || token.startsWith(`${o.value}x`),
  );
  const base = known ? known.value : token.split("x")[0];
  const rest = token.slice(base.length).replace(/^x/, "");
  const [b, bOther] = splitOption(base, bases);
  const variations = rest ? rest.split("x").filter(Boolean) : [];
  return {
    [`${prefix}_base`]: b,
    [`${prefix}_base_other`]: bOther,
    [`${prefix}_variation`]: variations,
  };
}
const splitCreative = (token, cfg) =>
  splitTwoLevel(token, "creative", cfg.creativeFormats, cfg.variationsFor);
const splitVisual = (token, cfg) =>
  splitTwoLevel(token, "visual", cfg.visualFormats, cfg.visualVariationsFor);

function formFromRequest(r, cfg) {
  return {
    ...INITIAL_FORM,
    for_app_name: r.for_app_name || "",
    language: r.language || "",
    account: r.account || "",
    auto_launch: r.auto_launch !== false,
    requested_for_agency_id: r.requested_for_agency_id
      ? String(r.requested_for_agency_id)
      : "",
    category: r.category || "",
    script_writer_name: r.script_writer_name || "",
    track: r.track || "",
    track_number: r.track_number != null ? String(r.track_number) : "",
    topic: r.topic || "",
    script_name: r.script_name || "",
    module_type: r.module_type || "",
    module: r.module ? String(r.module).split("x").filter(Boolean) : [],
    ...splitCreative(r.creative_format || "", cfg),
    ...splitVisual(r.visual_format || "", cfg),
    human_ai: r.human_ai || "",
    script_type: r.script_type || "",
    hooks_count: r.hooks_count != null ? String(r.hooks_count) : "1",
    priority: r.priority || "low",
    due_delivery_date: r.due_delivery_date
      ? new Date(r.due_delivery_date)
      : null,
    comments: r.comments || "",
    script: r.script || "",
    reference_link: r.reference_link || "",
    remarks: r.remarks || "",
  };
}

const PRIORITY_OPTIONS = [
  {
    value: "low",
    label: "Low",
    tone: "text-emerald-700 bg-emerald-50 border-emerald-200",
  },
  {
    value: "medium",
    label: "Medium",
    tone: "text-amber-700 bg-amber-50 border-amber-200",
  },
  {
    value: "high",
    label: "High",
    tone: "text-rose-700 bg-rose-50 border-rose-200",
  },
];

// Label + control + optional hint; every field in the form goes through this.
const Field = ({
  label,
  required,
  hint,
  trailing,
  className = "",
  children,
}) => (
  <div className={`space-y-1.5 ${className}`}>
    <div className="flex items-center justify-between gap-2">
      <Label className="text-[13px] text-gray-700">
        {label} {required && <Req />}
      </Label>
      {trailing}
    </div>
    {children}
    {hint && <p className="text-xs text-muted-foreground">{hint}</p>}
  </div>
);

// Numbered group of fields laid out in a two-column grid.
const Section = ({ step, title, description, children }) => (
  <section className="rounded-xl bg-white p-5 !border !border-gray-100">
    <div className="mb-4 flex items-start gap-3">
      <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-primaryPurple to-secondaryDarkPurple text-xs font-semibold text-white shadow-sm">
        {step}
      </span>
      <div>
        <h3 className="text-sm font-semibold text-gray-900">{title}</h3>
        <p className="text-xs text-muted-foreground">{description}</p>
      </div>
    </div>
    <div className="grid gap-4 sm:grid-cols-2">{children}</div>
  </section>
);

const Chip = ({ on, onClick, className = "", title, children }) => (
  <button
    type="button"
    aria-pressed={on}
    onClick={onClick}
    title={title}
    className={`inline-flex h-8 items-center rounded-full border px-3 text-sm font-medium transition-colors ${
      on
        ? "border-primary bg-primary text-primary-foreground shadow-sm"
        : "border-input bg-white text-gray-600 hover:border-gray-300 hover:bg-accent"
    } ${className}`}
  >
    {children}
  </button>
);

// One-of pills for short option lists; `tone` colours the active pill per option.
const Segmented = ({ value, onChange, options, tone = false }) => (
  <div className="flex flex-wrap gap-1.5">
    {options.map((o) => {
      const on = value === o.value;
      const active =
        tone && o.tone
          ? o.tone
          : "border-primary bg-primary text-primary-foreground shadow-sm";
      return (
        <button
          key={o.value}
          type="button"
          aria-pressed={on}
          onClick={() => onChange(o.value)}
          className={`h-9 flex-1 rounded-md border px-3 text-sm font-medium transition-colors ${on ? active : "border-input bg-white text-gray-600 hover:bg-accent"}`}
        >
          {o.label}
        </button>
      );
    })}
  </div>
);

const Pill = ({ children }) => (
  <span className="rounded-full bg-gray-100 px-2 py-0.5 font-mono text-[11px] text-gray-600">
    {children}
  </span>
);
const Dot = ({ className }) => (
  <span className={`inline-block h-2 w-2 rounded-full ${className}`} />
);

// Base dropdown (with Other) plus that base's variations as chips and a "+ New" chip for free text.
const TwoLevelPicker = ({
  label,
  prefix,
  form,
  setForm,
  bases,
  variationsFor,
  onNew,
}) => {
  const baseKey = `${prefix}_base`;
  const varKey = `${prefix}_variation`;
  const base = pickValue(form, baseKey);
  const variations = variationsFor(form[baseKey]);
  const chosen = form[varKey] || [];
  const presets = new Set(variations.map((o) => o.value));
  const customs = chosen.filter((v) => !presets.has(v));
  const toggle = (v) =>
    setForm((f) => ({
      ...f,
      [varKey]: chosen.includes(v)
        ? chosen.filter((x) => x !== v)
        : [...chosen, v],
    }));
  return (
    <>
      <SimpleSelect
        label={label}
        required
        value={form[baseKey] === OTHER ? "" : form[baseKey]}
        options={bases}
        onChange={(v) =>
          setForm((f) => ({
            ...f,
            [baseKey]: v,
            [`${baseKey}_other`]: "",
            [varKey]: [],
          }))
        }
        hint={
          form[baseKey] === OTHER
            ? `Stored value "${form[`${baseKey}_other`]}" is not in the config; pick a ${label.toLowerCase()}.`
            : null
        }
      />
      <Field
        label={`${label} variation`}
        required={variations.length > 0}
        hint={
          !base
            ? `Pick a ${label.toLowerCase()} first.`
            : variations.length
              ? "Pick any that apply, or add your own."
              : "No preset variations; add one if needed."
        }
      >
        <div className="flex flex-wrap items-center gap-1.5">
          {variations.map((o) => (
            <Chip
              key={o.value}
              on={chosen.includes(o.value)}
              onClick={() => toggle(o.value)}
            >
              {o.label}
            </Chip>
          ))}
          {customs.map((v) => (
            <Chip key={v} on onClick={() => toggle(v)} title="Click to remove">
              {v} ×
            </Chip>
          ))}
          {base && (
            <Chip
              on={false}
              onClick={() => onNew("")}
              className="border-dashed"
            >
              + New
            </Chip>
          )}
        </div>
      </Field>
    </>
  );
};

const MODULE_TYPE_HINTS = {
  cloning: "Replication of a competitor’s winning ad",
};

const SimpleSelect = ({
  label,
  value,
  options,
  onChange,
  required,
  disabled,
  placeholder,
  hint,
  className,
}) => (
  <Field label={label} required={required} hint={hint} className={className}>
    <Select value={value} onValueChange={onChange} disabled={disabled}>
      <SelectTrigger>
        <SelectValue
          placeholder={placeholder || `Select ${label.toLowerCase()}`}
        />
      </SelectTrigger>
      <SelectContent>
        {options.map((o) => (
          <SelectItem key={o.value} value={o.value} description={o.description}>
            {o.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  </Field>
);

// Same field chrome as SimpleSelect, with type-to-filter for long option lists.
const SearchSelect = ({
  label,
  value,
  options,
  onChange,
  required,
  disabled,
  placeholder,
  hint,
  emptyText,
  trailing,
  className,
}) => (
  <Field
    label={label}
    required={required}
    hint={hint}
    trailing={trailing}
    className={className}
  >
    <Combobox
      value={value}
      onChange={onChange}
      options={options}
      disabled={disabled}
      placeholder={placeholder || `Select ${label.toLowerCase()}`}
      searchPlaceholder={`Search ${label.toLowerCase()}…`}
      emptyText={emptyText || `No ${label.toLowerCase()} matches.`}
    />
  </Field>
);

const TextField = ({
  label,
  value,
  onChange,
  required,
  placeholder,
  maxLength,
  className,
}) => (
  <Field label={label} required={required} className={className}>
    <Input
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      maxLength={maxLength}
    />
  </Field>
);

const AdRequestCreate = ({ onSuccess, editRequest = null, direct = false }) => {
  const isEdit = !!editRequest?.id;
  const cfg = useAmsConfig();
  const { globalApp, globalLanguage } = useGlobalAppAndLanguage();
  const newForm = {
    ...INITIAL_FORM,
    for_app_name: globalApp,
    language: globalLanguage,
  };
  const [form, setForm] = useState(() =>
    editRequest ? formFromRequest(editRequest, cfg) : newForm,
  );
  const axiosPrivate = useAxiosPrivate();
  const queryClient = useQueryClient();
  const currentUser = useZustandStore((s) => s?.currentUser);
  const scopedAgencyId = getAgencyIdFromUser(currentUser);
  const agencyScoped = isAgencyScopedUser(currentUser);
  const { hasPermission, isSuperAdmin } = usePermissions();
  const canSetAutoLaunch = !agencyScoped || isSuperAdmin || direct;
  const canListAgencies = AGENCY_LIST_PERMISSIONS.some((p) => hasPermission(p));

  const { data: agenciesResult, isLoading: agenciesLoading } = useQuery({
    queryKey: GET_AGENCIES_QUERY_KEY,
    queryFn: () =>
      AgencyAPIs.list({
        axiosPrivate,
        status: "approved",
        limit: 100,
        offset: 0,
      }),
    enabled: !agencyScoped && canListAgencies,
  });
  const agencies = agenciesResult?.data ?? [];

  const { data: metaAccounts = [], isLoading: accountsLoading } = useQuery({
    queryKey: ["meta-ad-accounts"],
    queryFn: () => AdsAPIs.listMetaAdAccounts({ axiosPrivate }),
    staleTime: 5 * 60 * 1000,
  });
  const accountOptions = useMemo(() => {
    const opts = metaAccounts.map((a) => {
      const name =
        a.name || a.account_name || String(a.id ?? a.account_id ?? "");
      return { value: name, label: name };
    });
    // Keep an already-saved account selectable even if Meta no longer lists it.
    if (form.account && !opts.some((o) => o.value === form.account))
      opts.unshift({ value: form.account, label: form.account });
    return opts;
  }, [metaAccounts, form.account]);

  useEffect(() => {
    if (!isEdit && scopedAgencyId != null)
      setForm((f) => ({
        ...f,
        requested_for_agency_id: String(scopedAgencyId),
      }));
  }, [scopedAgencyId, isEdit]);

  useEffect(() => {
    if (!isEdit || !cfg.hasConfig) return;
    setForm((f) => ({
      ...f,
      ...splitCreative(composeTwoLevel(f, "creative"), cfg),
      ...splitVisual(composeTwoLevel(f, "visual"), cfg),
    }));
  }, [isEdit, cfg]);

  const selectedAgencyId = form.requested_for_agency_id
    ? Number(form.requested_for_agency_id)
    : null;

  const { data: ctmOptions } = useQuery({
    queryKey: [...GET_CTM_OPTIONS_QUERY_KEY, form.for_app_name, form.language],
    queryFn: () =>
      CategoryTrackMappingAPIs.options({
        axiosPrivate,
        app_name: form.for_app_name,
        language: form.language,
      }),
    enabled: !!form.for_app_name && !!form.language,
  });
  const ctmCategories = (ctmOptions?.categories ?? []).filter(
    (c) => c && c.trim(),
  );
  const ctmTracksByCategory = ctmOptions?.tracks_by_category ?? {};
  const ctmTracks = form.category
    ? (ctmTracksByCategory[form.category] ?? []).filter((t) => t?.track?.trim())
    : [];

  const languageOptions = cfg.languagesFor(form.for_app_name);
  const moduleOptions = cfg.modulesFor(form.module_type);

  const selectedAgencyName =
    agencies.find((a) => Number(a.id) === selectedAgencyId)?.name ||
    (isEdit
      ? editRequest?.requested_for_agency_name
      : currentUser?.agency_name) ||
    "";
  // Nomenclature uses the requester's username; a direct request takes the typed name.
  const requesterUsername =
    (isEdit ? editRequest?.requested_by_username : currentUser?.username) || "";
  const requesterSlot = direct ? form.requester_name : requesterUsername;

  const { data: adTypesResult, isLoading: adTypesLoading } = useQuery({
    queryKey: ["adtypes-for-agency", selectedAgencyId],
    queryFn: () =>
      AdsAPIs.listAdTypesForAgency({ axiosPrivate, agency_id: selectedAgencyId }),
    enabled: direct && !!selectedAgencyId,
  });
  const adTypeOptions = (adTypesResult?.data ?? []).map((t) => ({
    value: String(t.id),
    label: t.name,
  }));
  const hookLinks = Array.from(
    { length: Number(form.hooks_count) || 0 },
    (_, i) => form.hook_links[i] || "",
  );
  const setHookLink = (i, value) =>
    setForm((f) => {
      const links = [...f.hook_links];
      links[i] = value;
      return { ...f, hook_links: links };
    });

  const creativeBase = pickValue(form, "creative_base");
  const creativeVariation = form.creative_variation;
  const creativeFormat = composeTwoLevel(form, "creative");
  const visualBase = pickValue(form, "visual_base");
  const visualVariation = form.visual_variation;
  const visualFormat = composeTwoLevel(form, "visual");
  // A variation is required only when the config lists variations for that base.
  const needsVariation = (variations, base) => variations.length > 0 && !!base;
  const moduleToken = composeModule(
    form.module,
    moduleOptions.map((o) => o.value),
  );

  // Old requests keep their legacy name untouched; new ones auto-build until the requester edits.
  const legacyEdit = isEdit && isLegacyName(editRequest?.remarks);
  const [remarksEdited, setRemarksEdited] = useState(false);
  const [editName, setEditName] = useState(false);
  // Popup for a free-text variation; `prefix` is "creative" or "visual".
  const [newVariation, setNewVariation] = useState(null);
  const saveNewVariation = () => {
    const text = (newVariation?.text || "").trim();
    if (!sanitizeToken(text)) return toast.error("Enter a variation name.");
    const { prefix } = newVariation;
    setForm((f) => ({
      ...f,
      [`${prefix}_variation`]: [
        ...new Set([...(f[`${prefix}_variation`] || []), text]),
      ],
    }));
    setNewVariation(null);
  };
  const nameFields = {
    track: form.track,
    track_number: form.track_number,
    topic: form.topic,
    script_name: form.script_name,
    module_type: form.module_type,
    module: moduleToken,
    creative_format: creativeFormat,
    visual_format: visualFormat,
    human_ai: form.human_ai,
    agency_name: selectedAgencyName,
    script_type: form.script_type,
    requester_name: requesterSlot,
  };
  const builtName = buildRequestRemarks(nameFields);
  // Direct mode previews the final names: creator, editor and today's delivery date filled in.
  const directAdset = direct
    ? buildDeliveryAdName(
        buildRequestRemarks({
          ...nameFields,
          delivery_date: format(new Date(), "ddMMMyy"),
        }),
        { creator_name: form.creator_name, editor_name: form.editor_name },
      )
    : "";
  useEffect(() => {
    if (remarksEdited || legacyEdit) return;
    setForm((f) =>
      f.remarks === builtName ? f : { ...f, remarks: builtName },
    );
  }, [builtName, remarksEdited, legacyEdit]);

  const { mutate: create, isPending } = useMutation({
    mutationFn: (payload) => AdsAPIs.createAdRequest({ axiosPrivate, payload }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: GET_AD_REQUESTS_QUERY_KEY });
      toast.success("Ad request created.");
      setForm(newForm);
      setRemarksEdited(false);
      onSuccess?.();
    },
  });
  const { mutate: update, isPending: isUpdating } = useMutation({
    mutationFn: (payload) =>
      AdsAPIs.updateAdRequest({ axiosPrivate, id: editRequest.id, payload }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: GET_AD_REQUESTS_QUERY_KEY });
      queryClient.invalidateQueries({
        queryKey: [...GET_AD_REQUEST_QUERY_KEY, editRequest.id],
      });
      toast.success("Ad request updated.");
      onSuccess?.();
    },
  });
  const { mutate: createDirect, isPending: isDirectPending } = useMutation({
    mutationFn: (payload) =>
      AdsAPIs.createDirectAdRequests({ axiosPrivate, payload }),
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: GET_AD_REQUESTS_QUERY_KEY });
      queryClient.invalidateQueries({ queryKey: GET_AD_DELIVERIES_QUERY_KEY });
      const hooks = res?.delivery_count ?? 0;
      toast.success(
        `Request created and ${hooks} hook${hooks === 1 ? "" : "s"} sent to moderation.`,
      );
      setForm(newForm);
      onSuccess?.();
    },
    onError: (e) =>
      toast.error(
        (e?.response?.data?.error_message || e?.message || "Submit failed.").replace(/^Row 1: /, ""),
      ),
  });
  const saving = direct ? isDirectPending : isEdit ? isUpdating : isPending;

  const set = (key) => (v) => setForm((f) => ({ ...f, [key]: v }));
  const handleAppChange = (v) =>
    setForm((f) => ({
      ...f,
      for_app_name: v,
      language: "",
      category: "",
      track: "",
      track_number: "",
    }));
  const handleLanguageChange = (v) =>
    setForm((f) => ({
      ...f,
      language: v,
      category: "",
      track: "",
      track_number: "",
    }));
  const handleCategoryChange = (v) =>
    setForm((f) => ({ ...f, category: v, track: "", track_number: "" }));
  const handleTrackChange = (v) => {
    const match = (ctmTracksByCategory[form.category] ?? []).find(
      (t) => t.track === v,
    );
    setForm((f) => ({
      ...f,
      track: v,
      track_number:
        match?.track_number != null ? String(match.track_number) : "",
    }));
  };
  const handleModuleTypeChange = (v) =>
    setForm((f) => ({ ...f, module_type: v, module: [] }));
  const toggleModule = (value) =>
    setForm((f) => ({
      ...f,
      module: f.module.includes(value)
        ? f.module.filter((m) => m !== value)
        : [...f.module, value],
    }));

  const handleSubmit = (e) => {
    e.preventDefault();
    const need = (cond, msg) => (cond ? null : msg);
    const problem =
      need(form.requested_for_agency_id, "Select an agency.") ||
      need(form.for_app_name, "App is required.") ||
      need(form.language, "Language is required.") ||
      need(form.account, "Account is required.") ||
      need(form.category, "Category is required.") ||
      need(form.track, "Track is required.") ||
      need(form.topic.trim(), "Topic is required.") ||
      need(form.script_name.trim(), "Script name is required.") ||
      need(form.module_type, "Module type is required.") ||
      need(form.module.length, "Pick at least one module.") ||
      need(sanitizeToken(creativeBase), "Creative format is required.") ||
      need(
        !needsVariation(cfg.variationsFor(form.creative_base), creativeBase) ||
          creativeVariation.length,
        "Creative format variation is required.",
      ) ||
      need(sanitizeToken(visualBase), "Visual format is required.") ||
      need(
        !needsVariation(
          cfg.visualVariationsFor(form.visual_base),
          visualBase,
        ) || visualVariation.length,
        "Visual format variation is required.",
      ) ||
      need(form.human_ai, "Human / AI is required.") ||
      need(form.script_type, "Script type is required.") ||
      need(form.hooks_count, "Select the number of hooks.") ||
      need(direct || form.due_delivery_date, "Due delivery date is required.") ||
      need(direct || form.script.trim(), "Script is required.") ||
      (direct ? directProblem() : null);
    if (problem) return toast.error(problem);

    const payload = {
      requested_for_agency_id: Number(form.requested_for_agency_id),
      for_app_name: form.for_app_name,
      language: form.language,
      account: form.account,
      category: form.category,
      script_writer_name: form.script_writer_name,
      track: form.track,
      topic: form.topic,
      script_name: form.script_name,
      module_type: form.module_type,
      module: form.module,
      creative_format: { base: creativeBase, variation: creativeVariation },
      visual_format: { base: visualBase, variation: visualVariation },
      human_ai: form.human_ai,
      script_type: form.script_type,
      hooks_count: Number(form.hooks_count),
      priority: form.priority,
      due_delivery_date: format(form.due_delivery_date, "yyyy-MM-dd"),
      comments: form.comments,
      script: form.script,
      reference_link: form.reference_link,
      remarks: form.remarks,
      requested_via: "single_entry",
      ...(canSetAutoLaunch ? { auto_launch: form.auto_launch } : {}),
    };
    if (form.track_number !== "")
      payload.track_number = Number(form.track_number);
    if (direct) {
      const { due_delivery_date, requested_via, requested_for_agency_id, ...brief } = payload;
      return createDirect({
        ...(agencyScoped ? {} : { agency_id: requested_for_agency_id }),
        items: [
          {
            ...brief,
            requester_name: form.requester_name,
            ad_type_id: Number(form.ad_type_id),
            creator_name: form.creator_name.trim(),
            editor_name: form.editor_name.trim(),
            hooks: hookLinks.map((url) => ({ ad_asset_url: url.trim() })),
          },
        ],
      });
    }
    (isEdit ? update : create)(payload);
  };

  // Direct mode: requester, delivery fields and one valid link per hook.
  function directProblem() {
    if (!sanitizeToken(form.requester_name)) return "Requester is required.";
    if (!form.ad_type_id) return "Ad type is required.";
    if (creatorRequired(form.human_ai) && !form.creator_name.trim())
      return "Creator name is required for Human ads.";
    if (!form.editor_name.trim()) return "Editor name is required.";
    const bad = hookLinks.findIndex((url) => !validateAssetUrl(url).ok);
    if (bad < 0) return null;
    return validateAssetUrl(hookLinks[bad]).error || `Add the link for hook ${bad + 1}.`;
  }

  if (!agencyScoped && canListAgencies && agenciesLoading) {
    return (
      <div className="flex items-center justify-center min-h-[320px]">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-primary border-t-transparent" />
      </div>
    );
  }

  const preview = explainName(direct ? directAdset : form.remarks);
  const placementDone =
    form.for_app_name &&
    form.language &&
    form.account &&
    form.category &&
    form.track;

  return (
    <>
      <form onSubmit={handleSubmit} className="py-3">
        <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_220px]">
          <div className="min-w-0 space-y-4">
            <Section
              step={1}
              title="Placement"
              description="Where the ad runs and which agency makes it."
            >
              <SimpleSelect
                label="App"
                required
                value={form.for_app_name}
                onChange={handleAppChange}
                options={cfg.appNames}
              />
              <SimpleSelect
                label="Language"
                required
                value={form.language}
                onChange={handleLanguageChange}
                options={languageOptions}
                disabled={!form.for_app_name}
                placeholder={
                  form.for_app_name ? "Select language" : "Select an app first"
                }
              />
              <SearchSelect
                label="Account"
                required
                value={form.account}
                onChange={set("account")}
                options={accountOptions}
                disabled={accountsLoading}
                placeholder={
                  accountsLoading
                    ? "Loading Meta accounts…"
                    : "Select Meta ad account"
                }
              />
              {!agencyScoped && canListAgencies && (
                <SearchSelect
                  label="Agency"
                  required
                  value={form.requested_for_agency_id}
                  onChange={set("requested_for_agency_id")}
                  options={agencies
                    .filter((a) => !direct || a.allow_direct_delivery)
                    .map((a, i) => ({
                      value: String(a.id),
                      label: agencyDisplayName(a, i),
                    }))}
                  hint={
                    direct
                      ? "Only agencies allowed to create and deliver together."
                      : null
                  }
                />
              )}
              {!agencyScoped && !canListAgencies && (
                <p className="sm:col-span-2 text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-md px-3 py-2">
                  You don’t have permission to list agencies. Use an
                  agency-linked account, or ask an admin.
                </p>
              )}
              <SearchSelect
                label="Category"
                required
                value={form.category}
                onChange={handleCategoryChange}
                options={ctmCategories.map((c) => ({ value: c, label: c }))}
                disabled={!form.for_app_name || !form.language}
                placeholder={
                  form.for_app_name && form.language
                    ? "Select category"
                    : "Select app and language first"
                }
                hint={
                  form.for_app_name &&
                  form.language &&
                  ctmCategories.length === 0
                    ? "No categories mapped for this app and language. Add them in Category Track Mapping."
                    : null
                }
              />
              <SearchSelect
                label="Track"
                required
                value={form.track}
                onChange={handleTrackChange}
                options={ctmTracks.map((t) => ({
                  value: t.track,
                  label: `${t.track}${t.track_number != null ? ` (#${t.track_number})` : ""}`,
                }))}
                disabled={!form.category}
                placeholder={
                  form.category ? "Select track" : "Select a category first"
                }
                trailing={
                  form.track_number ? (
                    <Pill>track #{form.track_number}</Pill>
                  ) : null
                }
              />
            </Section>

            <Section
              step={2}
              title="Brief"
              description="What the ad is about and how it is made."
            >
              <TextField
                label="Topic"
                required
                value={form.topic}
                onChange={set("topic")}
                placeholder="Broad topic, e.g. meesho"
                maxLength={255}
              />
              <TextField
                label="Script name"
                required
                value={form.script_name}
                onChange={set("script_name")}
                placeholder="e.g. love propose x sandeep"
                maxLength={255}
              />
              <TextField
                label="Script writer"
                value={form.script_writer_name}
                onChange={set("script_writer_name")}
                placeholder="Who wrote the script"
                maxLength={128}
              />
              <Field label="Script type" required>
                <Segmented
                  value={form.script_type}
                  onChange={set("script_type")}
                  options={cfg.scriptTypes}
                />
              </Field>
              <SimpleSelect
                label="Module type"
                required
                value={form.module_type}
                onChange={handleModuleTypeChange}
                options={cfg.moduleTypes.map((m) => ({ ...m, description: MODULE_TYPE_HINTS[m.value] }))}
                hint={MODULE_TYPE_HINTS[form.module_type]}
              />
              <Field
                label="Module"
                required
                hint={
                  form.module_type
                    ? "Tick everything this request changes."
                    : "Pick a module type first."
                }
              >
                <div className="flex flex-wrap items-center gap-1.5">
                  {moduleOptions.map((o) => (
                    <Chip
                      key={o.value}
                      on={form.module.includes(o.value)}
                      onClick={() => toggleModule(o.value)}
                    >
                      {o.label}
                    </Chip>
                  ))}
                </div>
              </Field>
              <TwoLevelPicker
                label="Creative format"
                prefix="creative"
                form={form}
                setForm={setForm}
                bases={cfg.creativeFormats}
                variationsFor={cfg.variationsFor}
                onNew={(text) => setNewVariation({ prefix: "creative", text })}
              />
              <TwoLevelPicker
                label="Visual format"
                prefix="visual"
                form={form}
                setForm={setForm}
                bases={cfg.visualFormats}
                variationsFor={cfg.visualVariationsFor}
                onNew={(text) => setNewVariation({ prefix: "visual", text })}
              />
              <Field label="Human / AI" required>
                <Segmented
                  value={form.human_ai}
                  onChange={set("human_ai")}
                  options={cfg.humanAi}
                />
              </Field>
            </Section>

            <Section
              step={3}
              title="Delivery"
              description={
                direct
                  ? "Who asked for it, who made it, and a link for every hook."
                  : "How many hooks, how urgent, and by when."
              }
            >
              {direct && (
                <>
                  <TextField
                    label="Requester"
                    required
                    value={form.requester_name}
                    onChange={set("requester_name")}
                    placeholder="Who asked for this ad, e.g. nivedita"
                    maxLength={64}
                  />
                  <SimpleSelect
                    label="Ad type"
                    required
                    value={form.ad_type_id}
                    onChange={set("ad_type_id")}
                    options={adTypeOptions}
                    disabled={!selectedAgencyId || adTypesLoading}
                    placeholder={
                      !selectedAgencyId
                        ? "Select an agency first"
                        : adTypesLoading
                          ? "Loading ad types…"
                          : "Select ad type"
                    }
                    hint={
                      selectedAgencyId && !adTypesLoading && !adTypeOptions.length
                        ? "This agency has no ad types in its rate card."
                        : null
                    }
                  />
                  <TextField
                    label="Creator name"
                    required={creatorRequired(form.human_ai)}
                    value={form.creator_name}
                    onChange={set("creator_name")}
                    placeholder="Who made the ad"
                    maxLength={128}
                  />
                  <TextField
                    label="Editor name"
                    required
                    value={form.editor_name}
                    onChange={set("editor_name")}
                    placeholder="Who edited it"
                    maxLength={128}
                  />
                </>
              )}
              <Field label="Number of hooks" required className="sm:col-span-2">
                <div className="flex flex-wrap gap-1.5">
                  {HOOKS.map((n) => (
                    <Chip
                      key={n}
                      on={String(form.hooks_count) === String(n)}
                      onClick={() => set("hooks_count")(String(n))}
                      className="w-9 px-0 justify-center"
                    >
                      {n}
                    </Chip>
                  ))}
                </div>
              </Field>
              {direct ? (
                hookLinks.length > 0 && (
                  <div className="space-y-3 sm:col-span-2">
                    {hookLinks.map((url, i) => {
                      const check = validateAssetUrl(url);
                      return (
                        <div
                          key={i}
                          className="space-y-1.5 rounded-lg border border-gray-100 bg-gray-50/60 p-3"
                        >
                          <Label className="text-[13px] text-gray-700">
                            Hook {i + 1} link <Req />
                          </Label>
                          <Input
                            value={url}
                            onChange={(e) => setHookLink(i, e.target.value)}
                            placeholder="https://drive.google.com/file/d/…"
                            aria-invalid={!!url.trim() && !check.ok}
                          />
                          {url.trim() && check.error && (
                            <p className="text-xs text-red-600">{check.error}</p>
                          )}
                          <p
                            className="truncate font-mono text-[11px] text-gray-500"
                            title={`${directAdset}_hook${i + 1}`}
                          >
                            {directAdset}_hook{i + 1}
                          </p>
                        </div>
                      );
                    })}
                  </div>
                )
              ) : (
              <>
              <Field label="Priority">
                <Segmented
                  value={form.priority}
                  onChange={set("priority")}
                  options={PRIORITY_OPTIONS}
                  tone
                />
              </Field>
              <Field label="Due delivery date" required>
                <DatePicker
                  value={form.due_delivery_date}
                  onChange={set("due_delivery_date")}
                  endMonth={DUE_DATE_END_MONTH}
                  placeholder="Pick a date"
                />
              </Field>
              </>
              )}
              {canSetAutoLaunch && (
                <Field
                  label="Auto launch"
                  hint="AMS launches it automatically on its launch date. Turn off to launch it by hand."
                  className="sm:col-span-2"
                >
                  <div className="flex h-9 items-center gap-2">
                    <Switch
                      checked={form.auto_launch}
                      onCheckedChange={set("auto_launch")}
                      aria-label="Auto launch"
                    />
                    <span className="text-sm text-gray-700">
                      {form.auto_launch ? "On" : "Off"}
                    </span>
                  </div>
                </Field>
              )}
            </Section>

            <Section
              step={4}
              title="Content"
              description="The script the agency shoots from, plus anything to reference."
            >
              <Field label="Script" required={!direct} className="sm:col-span-2">
                <textarea
                  value={form.script}
                  onChange={(e) => set("script")(e.target.value)}
                  placeholder="Full script / transcript"
                  className={`${textareaCls} min-h-[140px]`}
                  rows={6}
                />
              </Field>
              <Field label="Comments" className="sm:col-span-2">
                <textarea
                  value={form.comments}
                  onChange={(e) => set("comments")(e.target.value)}
                  placeholder="Notes to the agency"
                  className={`${textareaCls} min-h-[70px]`}
                  rows={3}
                />
              </Field>
              <TextField
                label="Reference link"
                value={form.reference_link}
                onChange={set("reference_link")}
                placeholder="Reference link(s), comma separated"
                className="sm:col-span-2"
              />
            </Section>
          </div>

          <aside className="lg:sticky lg:top-0 lg:self-start space-y-3">
            <div className="rounded-xl border border-[#e3e0fa] bg-gradient-to-br from-[#efedfc] via-white to-white p-4 shadow-card">
              <div className="flex items-center justify-between">
                <p className="text-xs font-semibold uppercase tracking-wide text-secondaryDarkPurple">
                  Ad set name
                </p>
                {!legacyEdit && !direct && (
                  <button
                    type="button"
                    onClick={() => setEditName((v) => !v)}
                    className="text-xs font-medium text-primary hover:underline"
                  >
                    {editName ? "Done" : "Edit"}
                  </button>
                )}
              </div>
              {legacyEdit || editName ? (
                <textarea
                  value={form.remarks}
                  readOnly={legacyEdit}
                  onChange={(e) => {
                    setRemarksEdited(true);
                    set("remarks")(e.target.value);
                  }}
                  className={`${textareaCls} mt-3 break-all font-mono text-[12px] leading-[26px]`}
                  id="HideScroll"
                  rows={Math.max(
                    6,
                    Math.ceil((form.remarks || "").length / 20) + 1,
                  )}
                />
              ) : (
                <p className="mt-3 break-all font-mono text-sm leading-[25px] text-gray-300">
                  {preview.map((t) => (
                    <React.Fragment key={t.key}>
                      <span
                        title={t.key}
                        className={` text-[12.5px] rounded-sm px-0.5 ${
                          t.source === SOURCE_AGENCY
                            ? "bg-amber-100 text-amber-800"
                            : t.source === SOURCE_AUTO
                              ? "bg-sky-100 text-sky-800"
                              : t.isPlaceholder
                                ? "text-gray-400"
                                : "font-semibold text-gray-900"
                        }`}
                      >
                        {t.value || "…"}
                      </span>
                      _
                    </React.Fragment>
                  ))}
                  <span className="font-semibold text-gray-900">ext</span>
                </p>
              )}
              {legacyEdit ? (
                <p className="mt-3 text-xs text-muted-foreground">
                  This request predates the new naming; its name is kept as-is.
                </p>
              ) : (
                <ul className="mt-3 space-y-1 text-[11px] text-gray-500">
                  <li className="flex items-center gap-1.5">
                    <Dot className="bg-gray-900" /> from this form
                  </li>
                  <li className="flex items-center gap-1.5">
                    <Dot className="bg-amber-400" /> agency fills at delivery
                  </li>
                  <li className="flex items-center gap-1.5">
                    <Dot className="bg-sky-400" /> system fills at QC approval
                  </li>
                </ul>
              )}
            </div>
            <p className="px-1 text-xs text-muted-foreground">
              {placementDone
                ? "Placement set. Fill the brief to complete the name."
                : "Start with app, language and account."}
            </p>
          </aside>
        </div>

        <div className="mt-6 flex items-center justify-between gap-3">
          <p className="text-xs text-muted-foreground">
            <Req /> required
          </p>
          <Button
            type="submit"
            disabled={saving}
            className="min-w-[160px] bg-gradient-to-r from-primaryPurple to-secondaryDarkPurple text-white shadow-md hover:opacity-95"
          >
            {isEdit
              ? saving
                ? "Saving…"
                : "Save changes"
              : saving
                ? "Creating…"
                : direct
                  ? "Create & deliver"
                  : "Create ad request"}
          </Button>
        </div>
      </form>
      {newVariation && (
        <CustomModal
          open
          title="New variation"
          onClose={() => setNewVariation(null)}
          handleConfirmation={saveNewVariation}
          handleConfirmationBtnText="Add"
          className="lg:!w-[420px]"
        >
          <div className="space-y-2 py-2">
            <Label>
              Variation for{" "}
              {newVariation.prefix === "creative" ? creativeBase : visualBase}
            </Label>
            <Input
              autoFocus
              value={newVariation.text}
              onChange={(e) =>
                setNewVariation((n) => ({ ...n, text: e.target.value }))
              }
              placeholder="e.g. Handheld"
              maxLength={64}
            />
            {sanitizeToken(newVariation.text) && (
              <p className="font-mono text-xs text-muted-foreground">
                stored as{" "}
                {composeTwoLevel(
                  {
                    ...form,
                    [`${newVariation.prefix}_variation`]: [
                      ...form[`${newVariation.prefix}_variation`],
                      newVariation.text,
                    ],
                  },
                  newVariation.prefix,
                )}
              </p>
            )}
          </div>
        </CustomModal>
      )}
    </>
  );
};

export default AdRequestCreate;
